type Theme = 'light' | 'dark';

const root = document.documentElement;

function currentTheme(): Theme {
  return root.dataset['theme'] === 'dark' ? 'dark' : 'light';
}

function prefersDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function storedTheme(): Theme | null {
  try {
    const saved = sessionStorage.getItem('theme');
    return saved === 'light' || saved === 'dark' ? saved : null;
  } catch {
    return null;
  }
}

function applyTheme(theme: Theme): void {
  root.dataset['theme'] = theme;

  // Mirrors the inline bootstrap in head.html. These have to stay in sync with
  // the attribute or the next navigation paints the previous theme's canvas
  // before the new document's stylesheet applies.
  const canvas = theme === 'dark' ? '#0d0d0d' : '#ffffff';
  root.style.colorScheme = theme;
  root.style.backgroundColor = canvas;

  // head.html only emits this override when the stored choice disagrees with the
  // OS, so it may not exist yet. An unconditional meta outranks the media-query
  // pair below it, which is what we want once the visitor has chosen explicitly.
  let meta = document.getElementById('theme-color');
  if (!(meta instanceof HTMLMetaElement)) {
    meta = document.createElement('meta');
    meta.id = 'theme-color';
    (meta as HTMLMetaElement).name = 'theme-color';
    document.head.appendChild(meta);
  }
  (meta as HTMLMetaElement).content = canvas;

  const icon = document.getElementById('theme-icon');
  if (icon) icon.textContent = theme === 'dark' ? 'light_mode' : 'dark_mode';
}

export function initTheme(): void {
  // Follow the OS unless the visitor has toggled during this session.
  applyTheme(storedTheme() ?? (prefersDark() ? 'dark' : 'light'));

  document.getElementById('theme-toggle')?.addEventListener('click', () => {
    const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    try {
      sessionStorage.setItem('theme', next);
    } catch {
      /* private browsing — the toggle still works for this page view */
    }
  });

  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', (event) => {
    if (storedTheme() === null) applyTheme(event.matches ? 'dark' : 'light');
  });
}
